import * as React from 'react';
import { useLazyQuery } from "@apollo/react-hooks";
import gql from "graphql-tag";
import { Guid } from "guid-string";
import { AsyncLoadResult, AsyncLoadOptions } from '../../../../shared/abstractStore';
import { useCompleteQuestionnaireViewModelQuery, useCompleteQuestionnaireViewModelQueryVariables } from '../../generated/useCompleteQuestionnaireViewModelQuery';
import { questionnaireFieldsFragment } from '../../models/Questionnaire';
import { mainApiConfig } from '../../../../configure/mainApiConfig';
import { questionnaireSectionFieldsFragment } from '../../models/QuestionnaireSection';
import { questionFieldsFragment } from '../../models/Question';
import { learningUnitFieldsFragment } from '../../models/LearningUnit';
import { questionnaireRespondentSessionFieldsFragment } from '../../models/QuestionnaireRespondentSession';
import { questionResponseFieldsFragment } from '../../models/QuestionResponse';
import { profileFieldsFragment } from '../../models/Profile';

export interface CompleteQuestionnaireAsyncLoadOptions extends AsyncLoadOptions {
    userId?: string | null | undefined,
}


/**
 * Get a Questionnaire from the store along with any response information for a user.
 * @param id
 * @param options
 */
export function useCompleteQuestionnaireViewModel(id: string | undefined | null, options: CompleteQuestionnaireAsyncLoadOptions = {}): AsyncLoadResult<useCompleteQuestionnaireViewModelQuery> {
    // Query the data we need form the api.
    const [load, { data, loading, error, refetch }] = useLazyQuery<useCompleteQuestionnaireViewModelQuery, useCompleteQuestionnaireViewModelQueryVariables>(
        gql`
        query useCompleteQuestionnaireViewModelQuery ($id: ID!, $userId: ID!) {
            model: questionnaire(id: $id) {
                ...questionnaireFields

                questionnaireSections {
                    ...questionnaireSectionFields
                }

                questions {
                    ...questionFields
                }

            }

            learningUnit: learningUnit (questionnaireId: $id) {
                ...learningUnitFields
            }

            questionnaireRespondentSession: questionnaireRespondentSession(questionnaireId: $id, userId: $userId){
                ...questionnaireRespondentSessionFields

                responses: responses{
                    ...questionResponseFields
                }
            }

            profile: profile(userId: $userId){
                ...profileFields
            }
            
        }

        ${questionnaireFieldsFragment}
        ${questionnaireSectionFieldsFragment}
        ${questionFieldsFragment}
        ${learningUnitFieldsFragment}
        ${questionnaireRespondentSessionFieldsFragment}
        ${questionResponseFieldsFragment}
        ${profileFieldsFragment}
        `,
        {
            variables: {
                id: id ?? Guid.empty,
                userId: options.userId ?? Guid.empty,
            },
            fetchPolicy: options.fetchPolicy ?? mainApiConfig.defaultFetchPolicy,
            pollInterval: options.pollInterval,
            onCompleted: (data) => {
                if (options.onCompleted) {
                    options.onCompleted(data);
                }
            }
        }
    );

    // Run the query (unless we are being lazy).
    React.useEffect(() => {
        if (!options.lazy) {
            load();
        }
    }, [options.lazy, load])

    // Return the results.
    const refresh = React.useCallback(async () => { !refetch ? await load() : await refetch(); }, [refetch, load]);
    return React.useMemo(() => ({
        data: data ?? ({} as any),
        refresh: refresh,
        isLoading: loading,
        errors: error
    }), [data, refresh, loading, error]);
}
